<template>
  <div
    id="how-otivo-makes-money"
    class="bg-white flex flex-col max-w-screen"
    v-if="pageContent?.currentPage === 'howOtivoMakesMoney'">
    <Breadcrumbs current-page-name="How Otivo makes money" />
    <BaseSection class="bg-blue-5 relative">
      <div class="max-w-[800px] flex flex-col items-center m-auto py-mobileNavBarHeight">
        <h1
          class="w-full justify-center text-center flex"
          data-test="howOtivoMakesMoneyHeading"
          v-html="pageContent.howOtivoMakesMoneyHero.heading" />
        <div
          class="intro-1 justify-center text-center flex mt-[40px]"
          data-test="howOtivoMakesMoneySubHeading"
          v-html="pageContent.howOtivoMakesMoneyHero.subHeading" />
        <img
          :src="AlwaysOpen"
          alt="AlwaysOpen"
          class="m-auto mt-[40px]"
          data-test="alwaysOpenImage" />
        <div v-html="pageContent.howOtivoMakesMoneyHero.body" class="paragraph-1 mt-[40px]" />
      </div>
    </BaseSection>
    <BaseSection
      class="relative flex flex-col items-center justify-center bg-blue-6 gap-y-10"
      v-if="freemiumEnabled">
      <BorderBulge class="fill-blue-5" />
      <h3 class="h3 text-blue-1">Choose a plan</h3>
      <p class="mx-auto mt-10 text-center paragraph-1 max-w-[600px] text-grey-1">
        With traditional advice costing around $3,960* a year, Otivo offers a far more affordable
        way to get advice tailored to you.
      </p>
      <PricingTable />
      <div class="flex flex-col gap-y-10">
        <div>
          <p class="text-center text-sm font-light text-grey-1">
            Prices are in AUD and include GST. Using Otivo might be tax deductible — ask your
            accountant for details.
          </p>
          <p class="paragraph-4 text-center pt-[10px]">
            *Source: Adviser Ratings, 2024 Australian Advice Landscape Report
          </p>
        </div>
        <FreeToStartButton />
      </div>
    </BaseSection>
    <PersonalPricingTiles :is-pricing-page="false" v-else />
    <ThreeTileDisplay
      class="bg-primary"
      background-colour="primary"
      :top-curve-fill="userOnMobile() ? 'white' : undefined"
      :bottom-curve-fill="userOnMobile() ? 'tertiary' : undefined"
      title-colour="white"
      text-colour="white"
      :icon-boxes="pageContent.threeTileDisplay.iterableItems" />
  </div>
</template>

<script lang="ts" setup>
import AlwaysOpen from '@/components/SVGS/AlwaysOpen.svg?url'

import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue'
import ThreeTileDisplay from '@/components/BrandComponents/Otivo/ThreeTileDisplay.vue'
import { computed, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import { userOnMobile } from '@/composables/userOnMobile.ts'
import { useHead } from '@unhead/vue'
import BaseSection from '@/app/components/BaseSection.vue'
import BorderBulge from '@/app/components/BorderBulge.vue'
import PricingTable from '@/app/components/home/PricingTable.vue'
import FreeToStartButton from '@/app/components/home/FreeToStartButton.vue'
import { useFreemium } from '@/composables/freemium/useFreemium.ts'
import PersonalPricingTiles from '@/components/BrandComponents/Pricing/PersonalPricingTiles.vue'
import { createStandardMeta } from '@/utils/seo'

const store = useStore()
const freemiumEnabled = useFreemium().freemiumEnabled

onBeforeMount(() => store.dispatch('ContentStore/getContent', 'howOtivoMakesMoney'))

// Set comprehensive SEO for How Otivo Makes Money page
const seoMeta = createStandardMeta({
  title: 'How Otivo Makes Money | Otivo',
  description: 'Learn how Otivo makes money with subscriptions, no commissions, and transparent pricing. Understand the benefits for your financial future.',
  keywords: 'otivo business model, transparent pricing, subscription financial advice, no commissions'
})

useHead({
  ...seoMeta
})
const pageContent = computed(() => store.getters['ContentStore/pageContent'])
</script>
